'use client';

import { useState, useEffect, useRef } from 'react';

/**
 * Self-running product demo for the DXR landing hero.
 * Renders the REAL DXR app UI (dark #2E2E2E shell, #111 panels, Poppins) and
 * walks it through the product's three states to convey the core idea:
 *   1. Dictate   — radiologist dictates into the findings box (live transcript)
 *   2. Structure — DXR generates: right panel shows the "Generating…" loader
 *   3. Report    — the structured, sign-off-ready Final Report appears
 *
 * The left (dictation) panel stays constant; the right panel transitions
 * Empty → Generating → Report, mirroring the actual app flow.
 */

// Per-state display time (ms). Dictate is longest so the transcript finishes.
const SLIDE_MS = [4200, 900, 8000];

const DICTATION_TEXT =
  'Complains of right lower abdominal pain and fever. Appendix is distended (~16mm) with mural ' +
  'thickening, periappendiceal fat stranding and mild pelvic free fluid. Small appendicolith noted ' +
  'within lumen. As compared to the prior CECT abdomen dated 22/10/2025, there is interval development ' +
  'of mural hypoenhancement. No collection or pneumoperitoneum. Reactive RIF lymphadenopathy. ' +
  'Subcentimetric simple hepatic cyst in segment 6 of liver.';
const DICTATION_WORDS = DICTATION_TEXT.split(' ');
const WORD_MS = 55;

const STEPS = [
  { label: 'Dictate', caption: 'Speak the case naturally' },
  { label: 'Structure', caption: 'DXR organizes your words' },
  { label: 'Report', caption: 'Sign-off-ready in seconds' },
];

// Step-indicator layout: 'strip' (compact pills below), 'tabs' (segmented bar
// below), or 'right' (cards stacked to the right of the window).
const STEP_LAYOUT = 'tabs';

// ── Icons (match DXR's minimal line-icon style) ─────────────────────
const Ic = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" {...p} />
);
const ChevronDown = () => <Ic width="14" height="14"><path d="M6 9l6 6 6-6" /></Ic>;
const MicIcon = () => (
  <Ic width="15" height="15"><rect x="9" y="2" width="6" height="11" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v4" /></Ic>
);
const StopIcon = () => <svg width="12" height="12" viewBox="0 0 12 12"><rect width="12" height="12" rx="2" fill="currentColor" /></svg>;
const Redo = () => <Ic width="16" height="16"><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 15-6.7L21 13" /></Ic>;
const Download = () => <Ic width="16" height="16"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></Ic>;
const Copy = () => <Ic width="16" height="16"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></Ic>;
const ThumbUp = () => <Ic width="16" height="16"><path d="M7 11v9H4v-9zM7 11l4-7a2 2 0 0 1 3 2l-1 5h5a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 20 21H7" /></Ic>;
const ThumbDown = () => <Ic width="16" height="16" style={{ transform: 'rotate(180deg)' }}><path d="M7 11v9H4v-9zM7 11l4-7a2 2 0 0 1 3 2l-1 5h5a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 20 21H7" /></Ic>;
const Moon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" fill="#fff" /></svg>
);

// ── Live-streaming dictation (replays whenever step 0 is active) ─────
function Dictation({ active, running }) {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (active !== 0 || !running) {
      setShown(DICTATION_WORDS.length);
      return;
    }
    setShown(0);
    const t = setInterval(() => {
      setShown((n) => {
        if (n >= DICTATION_WORDS.length) {
          clearInterval(t);
          return n;
        }
        return n + 1;
      });
    }, WORD_MS);
    return () => clearInterval(t);
  }, [active, running]);

  const streaming = active === 0 && shown < DICTATION_WORDS.length;

  return (
    <span className="dxr-dictation" suppressHydrationWarning>
      {DICTATION_WORDS.slice(0, shown).map((w, i) => (
        <span key={i} className="dxr-word">{w} </span>
      ))}
      {streaming && <span className="dxr-caret" />}
    </span>
  );
}

// ── Right-panel: the structured Final Report ────────────────────────
// Modelled as lines of coloured segments so it can be streamed word-by-word,
// the way the real DXR report generates. Segment colour: lbl = white/bold/upper
// section label, b = blue body text, hl = amber highlight, title = report title.
const REPORT = [
  { type: 'title', segs: [{ c: 'title', t: 'CECT ABDOMEN AND PELVIS' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Technique:' }, { c: 'b', t: 'Axial images of the abdomen and pelvis were acquired following the administration of intravenous contrast. Multiplanar reformatted images were reviewed.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Clinical History:' }, { c: 'b', t: 'Complains of right lower abdominal pain and fever.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Comparison:' }, { c: 'b', t: 'CECT abdomen dated 22/10/2025.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Findings:' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Liver:' }, { c: 'hl', t: 'A subcentimetric simple hepatic cyst is noted in segment 6 of the liver.' }, { c: 'b', t: 'The remainder of the liver parenchyma is unremarkable in size, contour, and attenuation. No intrahepatic or extrahepatic biliary dilatation is seen.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Gallbladder:' }, { c: 'b', t: 'The gallbladder is unremarkable. There are no gallstones or wall thickening.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Spleen:' }, { c: 'b', t: 'The spleen is normal in size and shows homogeneous enhancement.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Pancreas:' }, { c: 'b', t: 'The pancreas is unremarkable in appearance. There is no evidence of peripancreatic inflammation.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Adrenal Glands:' }, { c: 'b', t: 'The adrenal glands are unremarkable bilaterally.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Kidneys and Ureters:' }, { c: 'b', t: 'The kidneys are normal in size, position, and enhancement. There is no evidence of hydronephrosis or renal calculi. The visualized portions of the ureters are unremarkable.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Appendix:' }, { c: 'hl', t: 'The appendix is distended, measuring approximately 16 mm in diameter, with associated mural thickening and periappendiceal fat stranding. A small appendicolith is noted within the lumen. Compared to the prior study, there is interval development of mural hypoenhancement.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Bowel and Mesentery:' }, { c: 'b', t: 'The visualized small and large bowel loops are otherwise unremarkable.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Lymph Nodes:' }, { c: 'hl', t: 'Reactive right iliac fossa lymphadenopathy is noted.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Peritoneum and Retroperitoneum:' }, { c: 'hl', t: 'Mild pelvic free fluid is present.' }, { c: 'b', t: 'No collection or pneumoperitoneum is seen. The retroperitoneum is otherwise clear.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Pelvic Organs:' }, { c: 'b', t: 'The visualized pelvic organs are unremarkable.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Bones:' }, { c: 'b', t: 'No acute fracture or suspicious osseous lesion is identified.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Visualized Lung Bases:' }, { c: 'b', t: 'The visualized lung bases are clear.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Impression:' }] },
  { type: 'line', segs: [{ c: 'b', t: '1.' }, { c: 'hl', t: 'Features of acute appendicitis with an appendicolith. Interval development of mural hypoenhancement suggests early gangrenous change.' }] },
  { type: 'line', segs: [{ c: 'b', t: '2.' }, { c: 'hl', t: 'Simple hepatic cyst.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Recommendation:' }, { c: 'hl', t: 'Urgent surgical consultation is recommended.' }] },
];

// Flatten to a word stream, each word tagged with its line index and colour.
const REPORT_WORDS = REPORT.flatMap((line, li) =>
  line.segs.flatMap((seg) => seg.t.split(' ').map((w) => ({ li, c: seg.c, w })))
);
const REPORT_WORD_MS = 22; // streaming pace of the generated report (fast — long report)

const SEG_CLASS = { lbl: 'lbl', b: 'b', hl: 'hl', title: '' };

function FinalReport({ running }) {
  const [n, setN] = useState(0);
  const endRef = useRef(null);

  useEffect(() => {
    if (!running) { setN(REPORT_WORDS.length); return; }
    const t = setInterval(() => {
      setN((k) => {
        if (k >= REPORT_WORDS.length) {
          clearInterval(t);
          return k;
        }
        return k + 1;
      });
    }, REPORT_WORD_MS);
    return () => clearInterval(t);
  }, [running]);

  // Auto-scroll the report box so the latest streamed line stays in view.
  useEffect(() => {
    const box = endRef.current?.closest('.dxr-right-body');
    if (box) box.scrollTop = box.scrollHeight;
  }, [n]);

  const streaming = n < REPORT_WORDS.length;
  const lastLi = n > 0 ? REPORT_WORDS[n - 1].li : -1;

  // Group the revealed words by their line.
  const byLine = new Map();
  for (let i = 0; i < n; i++) {
    const tok = REPORT_WORDS[i];
    if (!byLine.has(tok.li)) byLine.set(tok.li, []);
    byLine.get(tok.li).push(tok);
  }

  return (
    <div className="dxr-report" suppressHydrationWarning>
      {REPORT.map((line, li) => {
        const words = byLine.get(li);
        if (!words) return null;
        const inner = (
          <>
            {words.map((tok, j) => (
              <span key={j} className={`dxr-word ${SEG_CLASS[tok.c]}`.trim()}>{tok.w} </span>
            ))}
            {streaming && li === lastLi && <span className="dxr-caret" />}
          </>
        );
        if (line.type === 'title') return <div key={li} className="dxr-rep-title">{inner}</div>;
        if (line.type === 'sec') return <p key={li} className="dxr-rep-sec">{inner}</p>;
        return <p key={li} className="dxr-rep-line">{inner}</p>;
      })}
      <span ref={endRef} />
    </div>
  );
}

const SLIDE_COUNT = STEPS.length;

export default function DxrHeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [reduced, setReduced] = useState(false);
  const animate = !reduced && !hidden;

  // Reduced-motion → jump to the final report and stop streaming; also pause the
  // whole demo when the browser tab is hidden so it isn't a perpetual engine.
  useEffect(() => {
    const mq = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    if (mq && mq.matches) { setReduced(true); setActive(2); }
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Auto-advance through the slides, unless paused (press-hold), tab-hidden, or reduced-motion.
  useEffect(() => {
    if (paused || !animate) return;
    const t = setTimeout(() => setActive((i) => (i + 1) % SLIDE_COUNT), SLIDE_MS[active]);
    return () => clearTimeout(t);
  }, [active, paused, animate]);

  const generating = active === 1;
  const reportReady = active === 2;
  const recording = active === 0;

  // Press-and-hold to pause; release (anywhere) resumes auto-advance.
  const pauseWhileHeld = () => {
    setPaused(true);
    const resume = () => {
      setPaused(false);
      window.removeEventListener('pointerup', resume);
      window.removeEventListener('pointercancel', resume);
    };
    window.addEventListener('pointerup', resume);
    window.addEventListener('pointercancel', resume);
  };

  return (
    <section className="dxr-hero">
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <h1 className="dxr-hero-h1">
        You dictate. <span className="accent">DXR does the rest.</span>
      </h1>

      {/* ── App window + step indicators (layout switch) ── */}
      <div className={`dxr-stage layout-${STEP_LAYOUT}`} onPointerDown={pauseWhileHeld}>
      <div className="dxr-shell">
        {/* Header */}
        <div className="dxr-head">
          <div className="dxr-head-left">
            <span className="dxr-logo">NATOE <b>DXR</b></span>
            <span className="dxr-dark"><span className="dxr-dark-moon"><Moon /></span>Dark</span>
          </div>
          <div className="dxr-head-right">
            <span className="dxr-cases">48 Cases Left</span>
            <span className="dxr-hello">Hello Dr. Miller! <ChevronDown /></span>
          </div>
        </div>

        {/* Inner two-panel workspace */}
        <div className="dxr-inner">
          {/* LEFT — dictation */}
          <div className="dxr-left">
            <div className="dxr-left-top">
              <span className="dxr-muted">Templates <ChevronDown /></span>
              <span className="dxr-ai">AI</span>
            </div>

            <div className="dxr-dict-head">
              <span className="dxr-muted">Dictate or type findings</span>
              <span className="dxr-mictoggle">
                <span className="dxr-mic-opt">External&nbsp;Mic</span>
                <span className={`dxr-mic-opt is-active ${recording ? 'rec' : ''}`}>
                  {recording ? <><StopIcon /> Stop</> : <><MicIcon /> Start</>}
                </span>
              </span>
            </div>

            <div className="dxr-textarea">
              <Dictation active={active} running={animate} />
            </div>

            <button className="dxr-genbtn" type="button">
              {generating ? 'Generating…' : 'Generate Report'}
            </button>
          </div>

          {/* RIGHT — report */}
          <div className="dxr-right">
            {!generating && (
              <div className="dxr-right-head">
                <span className="dxr-muted">Final Report</span>
                <span className="dxr-restart"><Redo /> Restart</span>
              </div>
            )}

            <div className="dxr-right-body">
              {generating ? (
                <div className="dxr-loader">
                  <span className="dxr-spinner" />
                  <span className="dxr-loader-text">Generating Report…</span>
                </div>
              ) : reportReady ? (
                <FinalReport running={animate} />
              ) : (
                <div className="dxr-placeholder">Report content will appear here after you generate.</div>
              )}
            </div>

            {reportReady && (
              <div className="dxr-right-foot">
                <div className="dxr-foot-l">
                  <span className="dxr-iconbtn"><ThumbUp /></span>
                  <span className="dxr-iconbtn"><ThumbDown /></span>
                </div>
                <div className="dxr-foot-r">
                  <span className="dxr-dl"><Download /> Download</span>
                  <span className="dxr-cp"><Copy /> Copy</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step indicators */}
      <div className="dxr-steps">
        {STEPS.map((s, i) => (
          <button
            key={s.label}
            className={`dxr-step ${i === active ? 'is-active' : ''}`}
            onPointerDown={() => setActive(i)}
            onClick={() => setActive(i)}
            aria-label={s.label}
          >
            <span className="dxr-step-num">{i + 1}</span>
            <span className="dxr-step-text">
              <span className="dxr-step-label">{s.label}</span>
              <span className="dxr-step-caption">{s.caption}</span>
            </span>
            {i === active && (
              <span className="dxr-step-bar">
                <span className="dxr-step-bar-fill" style={{ animationDuration: `${SLIDE_MS[i]}ms` }} />
              </span>
            )}
          </button>
        ))}
      </div>
      </div>
    </section>
  );
}

const styles = `
.dxr-hero { max-width: 1240px; margin: 0 auto; padding: 12px 3% 8px; text-align: center; font-family: 'Inter', sans-serif; }
.dxr-hero-h1 { font-size: clamp(28px, 4.4vw, 50px); font-weight: 700; color: #fff; margin: 0 0 4px; letter-spacing: -0.02em; }
.dxr-hero-h1 .accent { color: #2997FF; }
.dxr-hero-h1 .sep { color: #2997FF; opacity: 0.55; font-weight: 400; margin: 0 0.12em; }
.dxr-hero-sub { font-size: clamp(14px, 1.6vw, 17px); line-height: 150%; color: rgba(255,255,255,0.7); max-width: 600px; margin: 0 auto 22px; }

/* ── DXR app shell ── */
.dxr-shell { font-family: 'Poppins', Inter, sans-serif; background: #2E2E2E; border-radius: 16px; padding: 12px; text-align: left; box-shadow: 0 30px 80px rgba(0,0,0,0.55); }
.dxr-head { display: flex; align-items: center; justify-content: space-between; height: 40px; padding: 0 6px; margin-bottom: 10px; }
.dxr-head-left { display: flex; align-items: center; gap: 14px; }
.dxr-logo { font-size: 14px; letter-spacing: 0.14em; color: #fff; font-weight: 600; }
.dxr-logo b { color: #2997FF; font-weight: 700; }
.dxr-dark { display: flex; align-items: center; gap: 7px; background: #3B3B3B; border-radius: 999px; padding: 4px 12px 4px 4px; font-size: 13px; color: #575757; }
.dxr-dark-moon { width: 22px; height: 22px; border-radius: 50%; background: #000; display: flex; align-items: center; justify-content: center; }
.dxr-head-right { display: flex; align-items: center; gap: 10px; }
.dxr-cases { background: #3B3B3B; color: #9E9E9E; font-size: 12.5px; font-weight: 600; border-radius: 40px; padding: 7px 13px; }
.dxr-hello { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.06); color: #9E9E9E; font-size: 13px; font-weight: 500; border-radius: 90px; padding: 7px 13px; }

.dxr-inner { display: flex; gap: 0; background: #111; border-radius: 10px; height: 400px; overflow: hidden; }
.dxr-left { flex: 0 0 47%; display: flex; flex-direction: column; padding: 18px 20px; border-right: 1px solid #222; }
.dxr-right { flex: 1; display: flex; flex-direction: column; padding: 18px 20px; min-width: 0; }
.dxr-muted { display: inline-flex; align-items: center; gap: 6px; color: #9E9E9E; font-size: 14px; font-weight: 500; }

.dxr-left-top { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #333; padding-bottom: 12px; margin-bottom: 14px; }
.dxr-ai { background: #2A2A2A; color: #9E9E9E; font-family: Inter, sans-serif; font-size: 13px; font-weight: 600; border-radius: 999px; padding: 5px 18px; }

.dxr-dict-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
.dxr-mictoggle { display: inline-flex; align-items: center; gap: 4px; background: #181818; border-radius: 999px; padding: 4px; }
.dxr-mic-opt { display: inline-flex; align-items: center; gap: 5px; font-family: Inter, sans-serif; font-size: 12px; font-weight: 500; color: #A0A1A0; border-radius: 999px; padding: 5px 12px; white-space: nowrap; }
.dxr-mic-opt.is-active { background: #DBE0FA; color: #111; }
.dxr-mic-opt.rec { color: #111; }

.dxr-textarea { flex: 1; background: #000; border: 1px solid rgba(255,255,255,0.37); border-radius: 8px; padding: 14px; margin-bottom: 14px; overflow: hidden; }
.dxr-dictation { color: #fff; font-size: clamp(11px, 1.2vw, 12.5px); line-height: 150%; }
.dxr-word { display: inline; animation: dxrWordIn 220ms ease both; }
@keyframes dxrWordIn { from { opacity: 0; } to { opacity: 1; } }
.dxr-caret { display: inline-block; width: 2px; height: 1.05em; background: #DBE0FA; margin-left: 1px; vertical-align: text-bottom; animation: dxrBlink 1s step-end infinite; }
@keyframes dxrBlink { 50% { opacity: 0; } }

.dxr-genbtn { width: 100%; height: 46px; border: none; border-radius: 999px; background: #DBE0FA; color: #111; font-family: 'Poppins', sans-serif; font-size: 17px; font-weight: 500; cursor: pointer; }

/* Right panel */
.dxr-right-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.dxr-restart { display: inline-flex; align-items: center; gap: 7px; color: #fff; font-size: 14px; font-weight: 500; }
.dxr-right-body { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow-y: auto; overflow-x: hidden; scrollbar-width: none; }
.dxr-right-body::-webkit-scrollbar { display: none; }
.dxr-placeholder { color: #575757; font-size: 14px; opacity: 0.8; }

.dxr-loader { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; }
.dxr-spinner { width: 38px; height: 38px; border-radius: 50%; border: 3px solid rgba(219,224,250,0.2); border-top-color: #DBE0FA; animation: dxrSpin 0.8s linear infinite; }
@keyframes dxrSpin { to { transform: rotate(360deg); } }
.dxr-loader-text { color: #9E9E9E; font-size: 15px; font-weight: 500; }

.dxr-report { font-size: clamp(10.5px, 1.15vw, 12.5px); line-height: 150%; animation: dxrReveal 700ms ease; }
@keyframes dxrReveal { from { opacity: 0; } to { opacity: 1; } }
.dxr-rep-title { text-align: center; font-weight: 700; text-decoration: underline; color: #fff; font-size: 1.08em; margin-bottom: 9px; letter-spacing: 0.03em; }
.dxr-rep-sec { margin: 6px 0 1px; }
.dxr-rep-line { margin: 1px 0 1px 8px; }
.dxr-report .b { color: #00D6FC; }
.dxr-report .hl { color: #FFCC75; font-weight: 600; }
.dxr-report .lbl { color: #fff; font-weight: 700; text-transform: uppercase; }

.dxr-right-foot { display: flex; align-items: center; justify-content: space-between; padding-top: 14px; }
.dxr-foot-l { display: flex; gap: 8px; }
.dxr-iconbtn { width: 36px; height: 36px; background: #1F1F1F; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; color: #fff; }
.dxr-foot-r { display: flex; gap: 10px; }
.dxr-dl, .dxr-cp { display: inline-flex; align-items: center; gap: 7px; border-radius: 8px; padding: 8px 16px; font-size: 15px; font-weight: 500; }
.dxr-dl { background: #1F1F1F; color: #fff; }
.dxr-cp { background: #fff; color: #111; }

/* Steps */
.dxr-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 16px; }
.dxr-step { position: relative; display: flex; align-items: flex-start; gap: 11px; text-align: left; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px 14px 14px; cursor: pointer; transition: border-color 200ms, background 200ms; font-family: 'Inter', sans-serif; overflow: hidden; }
.dxr-step.is-active { border-color: rgba(41,151,255,0.6); background: rgba(41,151,255,0.07); }
.dxr-step-num { flex: none; width: 26px; height: 26px; border-radius: 50%; background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.7); font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.dxr-step.is-active .dxr-step-num { background: #2997FF; color: #001; }
.dxr-step-text { display: flex; flex-direction: column; gap: 3px; }
.dxr-step-label { font-size: 15px; font-weight: 600; color: #fff; }
.dxr-step-caption { font-size: 12.5px; line-height: 145%; color: rgba(255,255,255,0.6); }
.dxr-step-bar { position: absolute; left: 0; bottom: 0; height: 3px; width: 100%; background: rgba(255,255,255,0.08); }
.dxr-step-bar-fill { display: block; height: 100%; background: #2997FF; animation: dxrStep linear forwards; }
@keyframes dxrStep { from { width: 0; } to { width: 100%; } }

/* ── Layout: slim strip (compact one-line pills below) ── */
.layout-strip .dxr-steps { margin-top: 14px; }
.layout-strip .dxr-step { align-items: center; gap: 9px; padding: 10px 16px; }
.layout-strip .dxr-step-caption { display: none; }
.layout-strip .dxr-step-num { width: 22px; height: 22px; font-size: 12px; }
.layout-strip .dxr-step-label { font-size: 14px; }

/* ── Layout: segmented tab bar below ── */
.layout-tabs .dxr-steps { grid-template-columns: none; display: flex; gap: 0; margin-top: 10px; border: 1px solid rgba(255,255,255,0.1); border-radius: 999px; overflow: hidden; background: rgba(255,255,255,0.03); }
.layout-tabs .dxr-step { flex: 1; justify-content: center; align-items: center; gap: 8px; border: none; border-radius: 0; background: transparent; padding: 7px 12px; }
.layout-tabs .dxr-step + .dxr-step { border-left: 1px solid rgba(255,255,255,0.08); }
.layout-tabs .dxr-step.is-active { background: rgba(41,151,255,0.1); }
.layout-tabs .dxr-step-num { width: 18px; height: 18px; font-size: 11px; }
.layout-tabs .dxr-step-text { flex-direction: row; align-items: baseline; gap: 0; }
.layout-tabs .dxr-step-label { font-size: 13.5px; line-height: 1; }
.layout-tabs .dxr-step-caption { display: none; }

/* ── Layout: compact cards stacked to the right (window stays wide) ── */
.layout-right { display: flex; gap: 16px; align-items: stretch; }
.layout-right .dxr-shell { flex: 1 1 auto; min-width: 0; }
.layout-right .dxr-steps { grid-template-columns: none; display: flex; flex-direction: column; gap: 9px; width: 158px; flex: none; margin-top: 0; justify-content: center; }
.layout-right .dxr-step { padding: 11px 12px; gap: 9px; }
.layout-right .dxr-step-num { width: 21px; height: 21px; font-size: 11px; }
.layout-right .dxr-step-label { font-size: 13px; }
.layout-right .dxr-step-caption { font-size: 11px; line-height: 138%; }
@media (max-width: 860px) {
  .layout-right { flex-direction: column; }
  .layout-right .dxr-steps { width: auto; }
}

@media (max-width: 768px) {
  .dxr-inner { flex-direction: column; height: auto; }
  .dxr-left { flex: none; border-right: none; border-bottom: 1px solid #222; }
  .dxr-textarea { min-height: 120px; }
  .dxr-steps { grid-template-columns: 1fr; }
  .dxr-head-right .dxr-hello { display: none; }
  /* Panels are full-width when stacked — bump the demo text to a legible size. */
  .dxr-dictation { font-size: 13.5px; }
  .dxr-report { font-size: 13px; }
}

/* Step tabs: stack number over label and drop captions on small screens so the
   three segments never clip in the segmented bar. */
@media (max-width: 560px) {
  .layout-tabs .dxr-steps { border-radius: 14px; }
  .layout-tabs .dxr-step { flex-direction: column; gap: 5px; padding: 10px 6px; text-align: center; }
  .layout-tabs .dxr-step-text { align-items: center; }
  .layout-tabs .dxr-step-caption { display: none; }
  .layout-tabs .dxr-step-label { font-size: 12.5px; }
  .layout-tabs .dxr-step-num { width: 22px; height: 22px; font-size: 12px; }
}
`;
